import { DatePicker, Input, RadioButtons, Select } from '@design-system';
import styles from './UpdatePassportDetails.module.scss';
import { useMemo } from 'react';
import { DocumentData } from 'src/shared/types/Document';
import { useGetReferenceData } from 'src/shared/queries/useGetReferenceData';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { useGetPassports } from 'src/shared/queries/useGetPassports';
import classNames from 'classnames';
import { PassportInfo } from 'src/shared/types/Case';

const TITLES = {
  passportDetails: 'Passport Details',
  countryLocation: 'Country/Location',
  issuedTo: 'Issued To',
  issuedDate: 'Issued Date',
  passportNumber: 'Passport Number',
  expirationDate: 'Expiration Date',
  issuingAuthority: 'Issuing Authority',
  issuedAtCountryLocation: 'Issued at Country/Location',
  issuedAtStateProvince: 'Issued at State/Province',
  issuedAtCity: 'Issued at City',
  typeOfPassport: 'Type of Passport',
  locationOfPassport: 'Location of Passport',
  atleast2BlankPages: 'Are there at least 2 blank pages in passport?',
  isEmergencyTemporaryPassport: 'Is it an emergency or temporary passport?',
  isBiometricPassport: 'Is it a biometric passport?',
};

const RADIO_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

export const PASSPORT_FIELD = {
  issuingCountry: 'country',
  issuedTo: 'beneFullName',
  issuedDate: 'issuedDate',
  passportNumber: 'passportNumber',
  passportExpirationDate: 'passportExpirationDate',
  issuingAuthority: 'issuingAuthority',
  passportIssuedCountry: 'passportIssuedCountry',
  issuedState: 'issuedState',
  issuedCity: 'issuedCity',
  passportType: 'passportType',
  locationOfPassport: 'locationOfPassport',
  hasEnoughBlanks: 'hasEnoughBlanks',
  isTemporary: 'isTemporary',
  isBiometric: 'isBiometric',
};

export const checkPassportNumberValid = (passports: PassportInfo[], value: string): boolean => {
  return passports.every((passport) => passport.passportNumber !== value);
};

interface UpdatePassportDetailsProps {
  documentData: DocumentData;
  disableCountry: boolean;
  disableIssuedTo: boolean;
  onPassportDetailsChanged: (
    fieldName: string,
    fieldValue: string | number | boolean | Date | null,
    isValid?: boolean
  ) => void;
}
const UpdatePassportDetails = ({ documentData, onPassportDetailsChanged }: UpdatePassportDetailsProps) => {
  const { data: passportLocations } = useGetReferenceData('LOCATION_OF_PASSPORT');
  const { data: passportTypes } = useGetReferenceData('TYPE_OF_PASSPORT');
  const { data: countries } = useGetCountries();
  const { data: user } = useGetUser();
  const { data: passportsData } = useGetPassports(Number(user?.beneId), documentData?.fields?.issuedTo as number);
  const passports = passportsData || [];
  const memoizedPassportLocations = useMemo(
    () =>
      passportLocations?.map(({ code, description }) => ({
        label: description,
        value: code,
      })) || [],
    [passportLocations]
  );

  const memoizedPassportTypes = useMemo(
    () =>
      passportTypes?.map(({ code, description }) => ({
        label: description,
        value: code,
      })) || [],
    [passportTypes]
  );

  const memoizedCountries = useMemo(
    () =>
      countries?.map(({ countryCode, countryName }) => ({
        label: countryName,
        value: countryCode,
      })) || [],
    [countries]
  );

  return (
    <div className={classNames(styles['update-passport-details'], 'mt-[24px]')}>
      <div className={classNames(styles['sub-title'], 'pb-[24px]')}>{TITLES.passportDetails}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
        <div className="md:col-span-1">
          <Select
            required
            label={TITLES.countryLocation}
            disabled
            options={memoizedCountries}
            selectedOption={{ label: '', value: documentData?.fields?.country as string }}
            onChange={() => {}}
            appendTo={document.body}
          />
        </div>

        <div className="md:col-span-1">
          <Input required label={TITLES.issuedTo} disabled value={documentData?.beneFullName} onChange={() => {}} />
        </div>

        <div className="md:col-span-1">
          <DatePicker
            value={documentData?.fields?.issuedDate}
            dateFormat={user?.userSettings?.dateFormat || ''}
            label={TITLES.issuedDate}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.issuedDate, e)}
          />
        </div>

        <div className="md:col-span-1">
          <Input
            label={TITLES.passportNumber}
            required
            value={documentData?.fields?.passportNumber}
            onChange={(e) =>
              onPassportDetailsChanged(
                PASSPORT_FIELD.passportNumber,
                e.target.value,
                checkPassportNumberValid(passports, e.target.value)
              )
            }
            validate={(e) => {
              return checkPassportNumberValid(passports, e);
            }}
            customErrorMessage={
              'The passport number you entered already exists. Please provide a different passport number.'
            }
          />
        </div>

        <div className="md:col-span-1">
          <DatePicker
            required
            value={documentData?.fields?.passportExpirationDate}
            dateFormat={user?.userSettings?.dateFormat || ''}
            label={TITLES.expirationDate}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.passportExpirationDate, e)}
          />
        </div>

        <div className="md:col-span-1">
          <Input
            label={TITLES.issuingAuthority}
            value={documentData?.fields?.issuingAuthority}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.issuingAuthority, e.target.value)}
          />
        </div>

        <div className="md:col-span-1">
          <Select
            label={TITLES.issuedAtCountryLocation}
            searchable={true}
            options={memoizedCountries}
            selectedOption={{ label: '', value: documentData?.fields?.passportIssuedCountry as string }}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.passportIssuedCountry, e?.value ?? null)}
            appendTo={document.body}
          />
        </div>

        <div className="md:col-span-1">
          <Input
            label={TITLES.issuedAtStateProvince}
            value={documentData?.fields?.issuedState}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.issuedState, e.target.value)}
          />
        </div>

        <div className="md:col-span-1">
          <Input
            label={TITLES.issuedAtCity}
            value={documentData?.fields?.issuedCity}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.issuedCity, e.target.value)}
          />
        </div>

        <div className="md:col-span-1">
          <Select
            label={TITLES.typeOfPassport}
            searchable={true}
            options={memoizedPassportTypes}
            selectedOption={{ label: '', value: documentData?.fields?.passportType as string }}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.passportType, e?.value ?? null)}
            appendTo={document.body}
          />
        </div>

        <div className="md:col-span-1">
          <Select
            label={TITLES.locationOfPassport}
            searchable={true}
            options={memoizedPassportLocations}
            selectedOption={{ label: '', value: documentData?.fields?.locationOfPassport as string }}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.locationOfPassport, e?.value ?? null)}
            appendTo={document.body}
          />
        </div>

        <div className="col-span-full">
          <div className={styles['radio-title']}>{TITLES.atleast2BlankPages}</div>
          <RadioButtons
            options={RADIO_OPTIONS}
            selectedOption={documentData?.fields?.hasEnoughBlanks}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.hasEnoughBlanks, e)}
            direction="row"
            type="secondary"
            name={TITLES.atleast2BlankPages}
            className={styles['radio-buttons']}
          />
        </div>

        <div className="col-span-full">
          <div className={styles['radio-title']}>{TITLES.isEmergencyTemporaryPassport}</div>
          <RadioButtons
            options={RADIO_OPTIONS}
            selectedOption={documentData?.fields?.isTemporary}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.isTemporary, e)}
            direction="row"
            type="secondary"
            name={TITLES.isEmergencyTemporaryPassport}
            className={styles['radio-buttons']}
          />
        </div>

        <div className="col-span-full">
          <div className={styles['radio-title']}>{TITLES.isBiometricPassport}</div>
          <RadioButtons
            options={RADIO_OPTIONS}
            selectedOption={documentData?.fields?.isBiometric}
            onChange={(e) => onPassportDetailsChanged(PASSPORT_FIELD.isBiometric, e)}
            direction="row"
            type="secondary"
            name={TITLES.isBiometricPassport}
            className={styles['radio-buttons']}
          />
        </div>
      </div>
    </div>
  );
};

export default UpdatePassportDetails;
